import type { FlagManager } from '@/state/flags/useFlag';
import type { PublishStart } from '@/state/flags/flag-status';
import { cn } from 'helpers/cn';
import { Input } from '@/components/ui/input';
import { Slider } from '@/components/ui/slider';
import { percentOf } from '@/state/flags/flag-status';
import { useRolloutControl } from '@/state/flags/useRolloutControl';
import { Meter } from '../../ConsolePanel';
import { FlagSection, FIELD_CLASS, SaveStrip } from './FlagSection';
import { FLAG_COPY } from '../copy';

type RolloutControlProps = { manager: FlagManager };

const COPY = FLAG_COPY.rollout;

/** Where publishing puts a draft's rollout, when that is not the share set here (publish-flag.ts). */
const draftNote = (start: PublishStart | null) =>
  start?.from === 'plan' ? COPY.draftNotePlan : start?.from === 'editor-default' ? COPY.draftNoteEditor : null;

/**
 * How many visitors get On, and a way to change it. The share is typed or slid and only sent on
 * Save, so dragging past 50% on the way to 20% reaches nobody; the figure follows the draft
 * meanwhile, marked as not saved, with what is live beside it. Turn off is the page's main
 * action, in the breadcrumb bar.
 */
export const RolloutControl = ({ manager }: RolloutControlProps) => {
  const control = useRolloutControl(manager, COPY);
  const { figure } = control;
  // An invalid share is said once, in the save strip.
  const note = control.isInvalid ? null : control.pausesPlan ? COPY.planPausesNote : draftNote(control.draftStart);

  return (
    <FlagSection
      title={COPY.title}
      help={COPY.help}
      description={COPY.description}
      tourAnchor="flag-rollout"
      footer={
        control.hasDraft ? (
          <SaveStrip
            blockingReason={control.isInvalid ? COPY.invalid : undefined}
            saveLabel={COPY.save}
            discardLabel={COPY.discard}
            onSave={control.save}
            onDiscard={control.discard}
            isSaving={control.isBusy}
            marksUnsaved={false}
          />
        ) : undefined
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <span className={cn('metric-value', figure.onBp > 0 ? 'text-foreground' : 'text-dim')}>
            {percentOf(figure.onBp)}
          </span>
          <span className="supporting">{COPY.getOn}</span>
          {figure.isUnsaved && (
            <>
              <span className="micro-label text-warn">{COPY.unsaved}</span>
              <span className="text-dim font-mono text-micro">{COPY.live(percentOf(figure.liveOnBp))}</span>
            </>
          )}
          {control.holdbackBp > 0 && !figure.isCapped && (
            <span className="supporting">{COPY.holdbackNote(percentOf(control.holdbackBp))}</span>
          )}
        </div>
        {figure.isCapped && (
          <p className="supporting m-0 -mt-2">
            {COPY.capped(percentOf(control.holdbackBp), percentOf(figure.maxOnBp))}
          </p>
        )}

        {/* The slider where the share can be changed; a meter where it cannot. */}
        {!control.canEdit && <Meter percent={figure.onBp / 100} className="bg-ok" />}

        {control.canEdit && (
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <Slider
              value={[control.sliderPercent]}
              min={0}
              max={100}
              step={1}
              disabled={control.isBusy}
              onValueChange={(value) => control.setFromSlider(value[0] ?? 0)}
              aria-label={COPY.title}
              className="sm:flex-1"
            />
            <div className="flex items-center gap-2">
              <Input
                value={control.text}
                onChange={(ev) => control.setText(ev.target.value)}
                onKeyDown={(ev) => {
                  if (ev.key === 'Enter') {
                    ev.preventDefault();
                    control.save();
                  }
                }}
                inputMode="decimal"
                disabled={control.isBusy}
                aria-label={`${COPY.title} %`}
                aria-invalid={control.isInvalid}
                className={cn(FIELD_CLASS, 'w-20 text-right font-mono')}
              />
              <span className="text-dim text-body">%</span>
            </div>
          </div>
        )}

        {note && <p className="supporting m-0">{note}</p>}
      </div>
    </FlagSection>
  );
};
